/* Printora V27 product lightbox zoom. Wheel/trackpad, pinch, double-click and drag-to-pan. */
(() => {
  'use strict';
  if (window.__printoraV27Zoom) return;
  window.__printoraV27Zoom = true;

  const MIN = 1;
  const MAX = 4;
  const KEY_STEP = 0.35;

  function bind(root) {
    if (!root || root.dataset.v27ZoomBound === '1') return;
    const box = root.querySelector('[data-v27-lightbox]');
    const stage = box?.querySelector('.pv27-lightbox__stage');
    const image = box?.querySelector('[data-v27-lightbox-image]');
    if (!box || !stage || !image) return;
    root.dataset.v27ZoomBound = '1';

    let scale = 1;
    let x = 0;
    let y = 0;
    let dragging = false;
    let dragX = 0;
    let dragY = 0;
    let startX = 0;
    let startY = 0;
    let pinchDistance = 0;

    function limits() {
      const iw = image.clientWidth || 0;
      const ih = image.clientHeight || 0;
      const sw = stage.clientWidth || 0;
      const sh = stage.clientHeight || 0;
      return {
        x: Math.max(0, (iw * scale - sw) / 2),
        y: Math.max(0, (ih * scale - sh) / 2)
      };
    }

    function clampPan() {
      if (scale <= 1.001) {
        x = 0;
        y = 0;
        return;
      }
      const max = limits();
      x = Math.max(-max.x, Math.min(max.x, x));
      y = Math.max(-max.y, Math.min(max.y, y));
    }

    function render() {
      clampPan();
      image.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
      const zoomed = scale > 1.01;
      box.classList.toggle('is-pv27-zoomed', zoomed);
      image.setAttribute('aria-label', zoomed ? 'Zoomed product image. Drag to move.' : 'Product image. Scroll or pinch to zoom.');
    }

    function reset() {
      scale = 1;
      x = 0;
      y = 0;
      dragging = false;
      pinchDistance = 0;
      box.classList.remove('is-pv27-dragging');
      render();
    }

    function zoomTo(next, clientX, clientY) {
      const previous = scale;
      const target = Math.max(MIN, Math.min(MAX, next));
      if (Math.abs(target - previous) < 0.001) return;

      const rect = stage.getBoundingClientRect();
      const cx = (clientX == null ? rect.left + rect.width / 2 : clientX) - rect.left - rect.width / 2;
      const cy = (clientY == null ? rect.top + rect.height / 2 : clientY) - rect.top - rect.height / 2;
      const ratio = target / previous;
      x = (x - cx) * ratio + cx;
      y = (y - cy) * ratio + cy;
      scale = target;

      if (scale <= 1.01) {
        scale = 1;
        x = 0;
        y = 0;
      }
      render();
    }

    stage.addEventListener('wheel', event => {
      if (box.hidden) return;
      event.preventDefault();
      const factor = Math.exp(-event.deltaY * 0.0018);
      zoomTo(scale * factor, event.clientX, event.clientY);
    }, { passive: false });

    stage.addEventListener('dblclick', event => {
      if (box.hidden) return;
      event.preventDefault();
      if (scale > 1.01) reset();
      else zoomTo(2.25, event.clientX, event.clientY);
    });

    image.addEventListener('pointerdown', event => {
      if (event.pointerType === 'touch' || scale <= 1.01 || box.hidden) return;
      event.preventDefault();
      dragging = true;
      startX = event.clientX;
      startY = event.clientY;
      dragX = x;
      dragY = y;
      box.classList.add('is-pv27-dragging');
      image.setPointerCapture?.(event.pointerId);
    });

    image.addEventListener('pointermove', event => {
      if (!dragging) return;
      x = dragX + event.clientX - startX;
      y = dragY + event.clientY - startY;
      render();
    });

    const stopDrag = () => {
      dragging = false;
      box.classList.remove('is-pv27-dragging');
    };
    image.addEventListener('pointerup', stopDrag);
    image.addEventListener('pointercancel', stopDrag);
    image.addEventListener('lostpointercapture', stopDrag);

    stage.addEventListener('touchstart', event => {
      if (box.hidden || event.touches.length !== 2) return;
      const a = event.touches[0];
      const b = event.touches[1];
      pinchDistance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }, { passive: true });

    stage.addEventListener('touchmove', event => {
      if (box.hidden || event.touches.length !== 2 || !pinchDistance) return;
      event.preventDefault();
      const a = event.touches[0];
      const b = event.touches[1];
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const midpointX = (a.clientX + b.clientX) / 2;
      const midpointY = (a.clientY + b.clientY) / 2;
      const factor = distance / pinchDistance;
      pinchDistance = distance;
      zoomTo(scale * factor, midpointX, midpointY);
    }, { passive: false });

    stage.addEventListener('touchend', event => {
      if (event.touches.length < 2) pinchDistance = 0;
    }, { passive: true });
    stage.addEventListener('touchcancel', () => { pinchDistance = 0; }, { passive: true });

    const observer = new MutationObserver(records => {
      if (records.some(record => record.type === 'attributes')) reset();
    });
    observer.observe(image, { attributes: true, attributeFilter: ['src'] });
    observer.observe(box, { attributes: true, attributeFilter: ['hidden'] });

    box.querySelector('[data-v27-lightbox-prev]')?.addEventListener('click', () => requestAnimationFrame(reset));
    box.querySelector('[data-v27-lightbox-next]')?.addEventListener('click', () => requestAnimationFrame(reset));
    box.querySelector('[data-v27-lightbox-close]')?.addEventListener('click', reset);

    document.addEventListener('keydown', event => {
      if (box.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        box.querySelector('[data-v27-lightbox-close]')?.click();
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        zoomTo(scale + KEY_STEP);
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault();
        zoomTo(scale - KEY_STEP);
      } else if (event.key === '0') {
        event.preventDefault();
        reset();
      } else if (event.key === 'ArrowLeft' && scale <= 1.01) {
        event.preventDefault();
        box.querySelector('[data-v27-lightbox-prev]')?.click();
      } else if (event.key === 'ArrowRight' && scale <= 1.01) {
        event.preventDefault();
        box.querySelector('[data-v27-lightbox-next]')?.click();
      }
    });

    window.addEventListener('resize', () => {
      if (!box.hidden) render();
    }, { passive: true });

    reset();
  }

  function boot(scope = document) {
    scope.querySelectorAll?.('[data-printora-v27]').forEach(bind);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', event => boot(event.target || document));
})();
